// components/PostEnquiries.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./PostEnquiries.css";

const PostEnquiries = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState({});

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState({
    posts: true,
    enquiries: false,
  });
  const [message, setMessage] = useState("");

  // Fetch user's posts on component mount
  useEffect(() => {
    fetchUserPosts();
  }, []);

  const fetchUserPosts = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await fetch(
        "https://ambatturindustrialestate.com/api/api/posts/user/",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // Filter only verified posts
        const verifiedPosts = data.filter((post) => post.is_verified === true);
        setPosts(verifiedPosts);
      } else {
        setMessage("Failed to fetch posts.");
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      setMessage("An error occurred while fetching posts.");
    } finally {
      setLoading((prev) => ({ ...prev, posts: false }));
    }
  };

  const fetchPostEnquiries = async (postId) => {
    setLoading((prev) => ({ ...prev, enquiries: true }));
    setMessage("");

    try {
      const token = localStorage.getItem("access");
      const response = await fetch(
        `https://ambatturindustrialestate.com/api/api/post-enquiries/post/${postId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // FILTER: Only show enquiries with is_verified: true
        // This hides all enquiries where is_verified is false
        const verifiedEnquiries = Array.isArray(data)
          ? data.filter((enquiry) => enquiry.is_verified === true)
          : [];

        setEnquiries(verifiedEnquiries);

        // Show message if no verified enquiries found
        if (verifiedEnquiries.length === 0) {
          setMessage("No verified enquiries found for this post.");
        }
      } else if (response.status === 404) {
        setEnquiries([]);
        setMessage("No enquiries found for this post.");
      } else {
        setMessage("Failed to fetch enquiries.");
      }
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      setMessage("An error occurred while fetching enquiries.");
    } finally {
      setLoading((prev) => ({ ...prev, enquiries: false }));
    }
  };

  const handlePostSelect = (post) => {
    setSelectedPost(post);
    fetchPostEnquiries(post.id);
  };
  const toggleMessage = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading.posts) {
    return (
      <div className="enquiries-container container mt-4">
        <div className="enquiries-loading d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enquiries-container container mt-4">
      <div className="enquiries-header d-flex justify-content-between align-items-center mb-4">
        <h2 className="enquiries-title">Post Enquiries</h2>
        <Link to="/posts" className="btn btn-secondary">
          Back to Posts
        </Link>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("successfully") ? "alert-success" : "alert-info"
          } alert-dismissible fade show`}
        >
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
          ></button>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <h5 className="card-title">No posts found</h5>
            <p className="card-text">
              You haven't created any verified posts yet.
            </p>
            <Link to="/posts/create" className="btn btn-primary">
              Create Your First Post
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Posts List Column */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Your Posts</h5>
              </div>
              <div className="list-group list-group-flush">
                {posts.map((post) => (
                  <button
                    key={post.id}
                    onClick={() => handlePostSelect(post)}
                    className={`list-group-item list-group-item-action ${
                      selectedPost?.id === post.id ? "active" : ""
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">{post.title}</h6>
                        <small
                          className={
                            selectedPost?.id === post.id
                              ? "text-white"
                              : "text-muted"
                          }
                        >
                          {post.post_type?.toUpperCase() || "GENERAL"}
                        </small>
                      </div>
                      <span className="badge bg-primary rounded-pill">
                        {post.quantity || "0"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Enquiries List Column */}
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  {selectedPost ? (
                    <>Enquiries for: {selectedPost.title}</>
                  ) : (
                    "Select a post to view enquiries"
                  )}
                </h5>
              </div>
              <div className="card-body">
                {loading.enquiries ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : selectedPost ? (
                  enquiries.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted mb-0">
                        No enquiries found for this post.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>Company</th>
                            <th>Message</th>
                            <th>Applied On</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enquiries.map((enquiry) => (
                            <tr key={enquiry.id}>
                              <td>{enquiry.name}</td>
                              <td>
                                <a href={`mailto:${enquiry.email}`}>
                                  {enquiry.email}
                                </a>
                              </td>
                              <td>
                                <a href={`tel:${enquiry.mobile}`}>
                                  {enquiry.mobile}
                                </a>
                              </td>
                              <td>{enquiry.company_name || "-"}</td>
                              {/* <td>
                                <span
                                  className="text-truncate d-inline-block"
                                  style={{ maxWidth: "150px" }}
                                  title={enquiry.message}
                                >
                                  {enquiry.message?.length > 30
                                    ? `${enquiry.message.substring(0, 30)}...`
                                    : enquiry.message}
                                </span>
                              </td> */}
                              <td>
                                <div>
                                  {expandedMessages[enquiry.id] ? (
                                    <span>{enquiry.message}</span>
                                  ) : (
                                    <span>
                                      {enquiry.message?.length > 30
                                        ? `${enquiry.message.substring(0, 30)}...`
                                        : enquiry.message}
                                    </span>
                                  )}

                                  {enquiry.message?.length > 30 && (
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 ms-2 read-more-btn"
                                      onClick={() => toggleMessage(enquiry.id)}
                                    >
                                      {expandedMessages[enquiry.id]
                                        ? "Read Less"
                                        : "Read More"}
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td>{formatDate(enquiry.created_at)}</td>
                              <td>
                                {enquiry.is_verified ? (
                                  <span className="badge bg-success">
                                    Verified
                                  </span>
                                ) : (
                                  <span className="badge bg-warning text-dark">
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">
                      Click on a post from the list to view its enquiries.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostEnquiries;
